import React, { useState } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { Popover, Button, Spin } from "antd";
import { Table as ExpandedTable, Thead } from "components/Table";
import { useQuery } from "@apollo/client";
import moment from "moment";

import i18n from "i18n";
import { formatPrice } from "utils/helpers";

import { DELIVERY_QUERY } from "../inventoriesQueries";

const DELIVERY_TABLE_HEADER = [
  i18n.t("warehouse.Inventories.DeliveryPopover.Table.Date"),
  i18n.t("warehouse.Inventories.DeliveryPopover.Table.Quantity"),
  i18n.t("warehouse.Inventories.DeliveryPopover.Table.Price"),
  i18n.t("warehouse.Inventories.DeliveryPopover.Table.Supplier"),
  i18n.t("warehouse.Inventories.DeliveryPopover.Table.Username"),
];

const MOVING_TABLE_HEADER = [
  i18n.t("warehouse.Inventories.DeliveryPopover.Table.Date"),
  i18n.t("warehouse.Inventories.DeliveryPopover.Table.Quantity"),
  i18n.t("warehouse.Inventories.DeliveryPopover.Table.LocationFrom"),
  i18n.t("warehouse.Inventories.DeliveryPopover.Table.Username"),
];

const Container = styled.div`
  width: 650px;
  max-height: 250px;

  b {
    padding-left: 20px;
    padding-top: 10px;
    display: block;
  }

  table {
    box-shadow: unset;
  }
`;

const StyledButton = styled(Button)`
  border: none;
  background-color: transparent;
  box-shadow: none;

  & > span {
    margin-right: 4px;
  }

  i > svg {
    margin-bottom: -1px;
  }
`;

const Placeholder = styled.div`
  text-align: center;
  padding-bottom: 10px;
  padding-top: 10px;
`;

const StyledTable = styled(ExpandedTable)`
  width: 100%;

  tr > th {
    padding: 10px 8px 8px 8px;
  }

  tr:nth-child(odd) > td {
    background-color: #effaff;
  }

  td {
    padding: 2px 0;

    :first-child {
      padding-left: 12px;
    }

    :last-child {
      padding-left: 12px;
    }
  }
`;

const Content = ({ t, inventory, inventoryCount: { product, last_inventory_count } }) => {
  const { data, loading } = useQuery(DELIVERY_QUERY, {
    variables: {
      product_id: product.id,
      location_id: inventory.location_id,
      datetime: last_inventory_count
        ? { _gte: last_inventory_count.datetime, _lte: inventory.datetime }
        : { _lte: inventory.datetime },
    },
  });

  if (loading) {
    return (
      <Container>
        <center>
          <Spin />
        </center>
      </Container>
    );
  }

  return (
    <Container>
      {data.delivery_products.length > 0 ? (
        <>
          <b>
            {t("warehouse.Inventories.DeliveryPopover.DeliveryTable.Title")}
          </b>
          <StyledTable index>
            <Thead columns={DELIVERY_TABLE_HEADER} />
            <tbody>
              {data.delivery_products.map((deliveryProduct) => (
                <tr key={deliveryProduct.id}>
                  <td width={200}>
                    {moment(deliveryProduct.delivery.datetime).format("lll")}
                  </td>
                  <td width={70} align="center">
                    {deliveryProduct.quantity}
                    &nbsp;{t(`const:unit_type.${product.unit}`)}
                  </td>
                  <td width={100}>
                    {formatPrice(deliveryProduct.quantity * deliveryProduct.price)}
                  </td>
                  <td>{deliveryProduct.delivery.supplier.name}</td>
                  <td>{deliveryProduct.delivery.user.name}</td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </>
      ) : (
        <Placeholder>
          {t("warehouse.Inventories.DeliveryPopover.DeliveryTable.Placeholder")}
        </Placeholder>
      )}
      {data.moving_products.length > 0 ? (
        <>
          <b>
            {t("warehouse.Inventories.DeliveryPopover.MovingTable.Title")}
          </b>
          <StyledTable index>
            <Thead columns={MOVING_TABLE_HEADER} />
            <tbody>
              {data.moving_products.map((movingProduct) => (
                <tr key={movingProduct.id}>
                  <td width={200}>
                    {moment(movingProduct.moving.datetime).format("lll")}
                  </td>
                  <td width={70} align="center">
                    {movingProduct.quantity}
                    &nbsp;{t(`const:unit_type.${product.unit}`)}
                  </td>
                  <td>
                    {t(
                      "warehouse.Inventories.DeliveryPopover.MovingTable.FromLocation", {
                        location: movingProduct.moving.location_from.name,
                      },
                    )}
                  </td>
                  <td>{movingProduct.moving.user.name}</td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </>
      ) : (
        <Placeholder>
          {t("warehouse.Inventories.DeliveryPopover.MovingTable.Placeholder")}
        </Placeholder>
      )}
    </Container>
  );
};

Content.propTypes = {
  t: PropTypes.func.isRequired,
  inventory: PropTypes.object.isRequired,
  inventoryCount: PropTypes.object.isRequired,
};

const DeliveryPopover = ({ t, inventory, inventoryCount }) => {
  const [isVisible, setVisible] = useState(false);
  const { deliveries, movings_in, product } = inventoryCount;

  const quantity = deliveries + movings_in;
  const quantityText = quantity === 0
    ? `${quantity} ${t(`const:unit_type.${product.unit}`)}`
    : `+${quantity} ${t(`const:unit_type.${product.unit}`)}`;

  return (
    <Popover
      content={<Content t={t} inventory={inventory} inventoryCount={inventoryCount} />}
      visible={isVisible}
      onVisibleChange={setVisible}
      trigger="click"
      placement="bottom"
    >
      <StyledButton>
        <span>{quantityText}</span>
        <i>
          <svg
            viewBox="0 0 1024 1024"
            focusable="false"
            data-icon="caret-down"
            width="10px"
            height="10px"
            fill="#7f7f7f"
            aria-hidden="true"
          >
            <path
              d="M840.4 300H183.6c-19.7 0-30.7 20.8-18.5 35l328.4 380.8c9.4 10.9 27.5 10.9 37 0L858.9 335c12.2-14.2 1.2-35-18.5-35z"
            />
          </svg>
        </i>
      </StyledButton>
    </Popover>
  );
};

DeliveryPopover.propTypes = {
  inventoryCount: PropTypes.object.isRequired,
  inventory: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

export default DeliveryPopover;
